import { GoldenSourceHelper } from "../../../app/Switch_Core_React_Standards";
import { dbPayload } from "../../../auth/registered/SEC_00_DB_Payload";

/**
 * 🛰️ GET PROFILE
 * Returns null if the caller hasn't captured FullName/DateOfBirth yet.
 */
export const getRepresentativeProfile = async (config) => {
    try {
        const rawData = await dbPayload(
            "/api/v1/sustina/representative/get-profile",
            "POST",
            config.siteKey,
            {}
        );

        const res = GoldenSourceHelper.normalize(rawData);
        return res.success ? res.data : null;
    } catch (err) {
        console.error("Representative Profile Fetch Failed:", err);
        return null;
    }
};

/**
 * 🛰️ SAVE PROFILE
 */
export const saveRepresentativeProfile = async (fullName, dateOfBirth, config) => {
    try {
        const payload = { FullName: fullName, DateOfBirth: dateOfBirth };

        const rawData = await dbPayload(
            "/api/v1/sustina/representative/save-profile",
            "POST",
            config.siteKey,
            GoldenSourceHelper.prepareForApi(payload)
        );

        const res = GoldenSourceHelper.normalize(rawData);
        if (!res.success) throw new Error(res.message || "Profile save failed.");
        return res.data;
    } catch (err) {
        console.error("Representative Profile Save Error:", err);
        throw err;
    }
};

/**
 * 🛰️ REGISTER COMPANY (single hit): Name + registered Address (Line1/2, Town,
 * County, Postcode, Country) + Reg Number, submitted together. Creates the
 * CorporateEntity + its Address row and grants REPRESENTATIVE in one call.
 * Server runs a quiet alignment check (company name vs caller's email domain)
 * before creating anything — a rejection here is final, not a client-side
 * validation to retry around.
 */
export const registerCompanyFull = async (company, config) => {
    try {
        const payload = {
            CompanyName: company.name,
            RegNumber: company.regNumber,
            Country_ISO2: company.country,
            AddressLine1: company.addressLine1,
            AddressLine2: company.addressLine2 || null,
            Town: company.town,
            County: company.county || null,
            Postcode: company.postcode,
            // Provenance for future reference - see SEC_RepresentativeController's
            // 3-case model (REGISTRY_CONFIRMED/SELF_DECLARED, REGISTRY/
            // GOOGLE_VALIDATED/MANUAL_UNVERIFIED).
            RegIdSourceCode: company.regIdSourceCode || null,
            AddressSourceCode: company.addressSourceCode || null,
        };

        const rawData = await dbPayload(
            "/api/v1/sustina/representative/register-company-full",
            "POST",
            config.siteKey,
            GoldenSourceHelper.prepareForApi(payload)
        );

        const res = GoldenSourceHelper.normalize(rawData);
        if (!res.success) throw new Error(res.message || "Company registration failed.");
        return res.data;
    } catch (err) {
        console.error("Representative Company Registration Error:", err);
        throw err;
    }
};

/**
 * 🛰️ REGISTER CHILD COMPANY (single hit): identical shape to registerCompanyFull,
 * plus parentEntityId. Used by "Manage Child Companies" on the Company Dashboard —
 * same Name/Address/Reg Number capture, but the new entity is created as a child
 * of the caller's already-represented company (no separate alignment check or
 * Representative grant on the backend).
 */
export const registerChildCompanyFull = async (company, parentEntityId, config) => {
    try {
        const payload = {
            ParentEntityId: parentEntityId,
            CompanyName: company.name,
            RegNumber: company.regNumber,
            Country_ISO2: company.country,
            AddressLine1: company.addressLine1,
            AddressLine2: company.addressLine2 || null,
            Town: company.town,
            County: company.county || null,
            Postcode: company.postcode,
            RegIdSourceCode: company.regIdSourceCode || null,
            AddressSourceCode: company.addressSourceCode || null,
        };

        const rawData = await dbPayload(
            "/api/v1/sustina/representative/create-child-company-full",
            "POST",
            config.siteKey,
            GoldenSourceHelper.prepareForApi(payload)
        );

        const res = GoldenSourceHelper.normalize(rawData);
        if (!res.success) throw new Error(res.message || "Child company registration failed.");
        return res.data;
    } catch (err) {
        console.error("Representative Child Company Registration Error:", err);
        throw err;
    }
};

/**
 * 🛰️ SIGN ATTESTATION (idempotent — one row per clause)
 */
export const signRepresentativeAttestation = async (corporateEntityId, clauseKey, s3Key, config) => {
    try {
        const payload = { CorporateEntityId: corporateEntityId, ClauseKey: clauseKey, S3Key: s3Key || null };

        const rawData = await dbPayload(
            "/api/v1/sustina/representative/sign-attestation",
            "POST",
            config.siteKey,
            GoldenSourceHelper.prepareForApi(payload)
        );

        const res = GoldenSourceHelper.normalize(rawData);
        if (!res.success) throw new Error(res.message || "Attestation signing failed.");
        return res.data;
    } catch (err) {
        console.error("Representative Attestation Sign Error:", err);
        throw err;
    }
};

/**
 * 🛰️ GET ATTESTATIONS (resume mid-flow)
 */
export const getRepresentativeAttestations = async (corporateEntityId, config) => {
    try {
        const payload = { CorporateEntityId: corporateEntityId };

        const rawData = await dbPayload(
            "/api/v1/sustina/representative/get-attestations",
            "POST",
            config.siteKey,
            GoldenSourceHelper.prepareForApi(payload)
        );

        const res = GoldenSourceHelper.normalize(rawData);
        return res.success ? res.data : [];
    } catch (err) {
        console.error("Representative Attestations Fetch Failed:", err);
        return [];
    }
};
